import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trash2,
  Plus,
  Play,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  LayoutDashboard,
  List,
  History,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  X,
  Loader2,
  Pencil,
  Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axiosInstance from '../utils/axiosInstance';
import { ShippySpinner } from '../components/ShippyLoader';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <div className="pt-6">{children}</div>;
}

const Benchmark = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  // Test cases state
  const [testCases, setTestCases] = useState([]);
  const [newOrderNumber, setNewOrderNumber] = useState('');
  const [newExpectedCost, setNewExpectedCost] = useState('');
  const [newCarrier, setNewCarrier] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Benchmark runs state
  const [runs, setRuns] = useState([]);
  const [expandedRunId, setExpandedRunId] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchTestCases = useCallback(async () => {
    try {
      const response = await axiosInstance.get('api/benchmark/test-cases/');
      if (response.data.status === 'success') {
        setTestCases(response.data.test_cases);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch test cases');
    }
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const response = await axiosInstance.get('api/benchmark/runs/');
      if (response.data.status === 'success') {
        setRuns(response.data.runs);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch benchmark runs');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchTestCases(), fetchRuns()]).finally(() => setLoading(false));
  }, [fetchTestCases, fetchRuns]);

  const handleAddTestCase = async () => {
    if (!newOrderNumber.trim() || !newExpectedCost.trim()) return;

    try {
      const response = await axiosInstance.post('api/benchmark/test-cases/', {
        order_number: newOrderNumber.trim(),
        expected_shipping_cost: parseFloat(newExpectedCost),
        carrier: newCarrier.trim(),
        notes: newNotes.trim(),
      });
      if (response.data.status === 'success') {
        setTestCases([...testCases, response.data.test_case]);
        setNewOrderNumber('');
        setNewExpectedCost('');
        setNewCarrier('');
        setNewNotes('');
        setSuccess('Test case added');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add test case');
    }
  };

  const handleDeleteTestCase = async (id) => {
    try {
      await axiosInstance.delete(`api/benchmark/test-cases/${id}/`);
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (editingId === id) setEditingId(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete test case');
    }
  };

  const startEditing = (tc) => {
    setEditingId(tc.id);
    setEditValues({
      order_number: tc.order_number,
      expected_shipping_cost: String(tc.expected_shipping_cost),
      carrier: tc.carrier || '',
      notes: tc.notes || '',
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSaveTestCase = async () => {
    if (!editingId) return;
    try {
      const response = await axiosInstance.put(`api/benchmark/test-cases/${editingId}/`, {
        order_number: editValues.order_number.trim(),
        expected_shipping_cost: parseFloat(editValues.expected_shipping_cost),
        carrier: editValues.carrier.trim(),
        notes: editValues.notes.trim(),
      });
      if (response.data.status === 'success') {
        setTestCases(testCases.map((tc) => (tc.id === editingId ? response.data.test_case : tc)));
        setEditingId(null);
        setEditValues({});
        setSuccess('Test case updated');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update test case');
    }
  };

  const handleRunBenchmark = async () => {
    if (testCases.length === 0) {
      setError('Add at least one test case before running a benchmark');
      return;
    }

    setRunning(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axiosInstance.post('api/benchmark/run/', {});
      if (response.data.status === 'started' || response.data.status === 'success') {
        const runId = response.data.run.id;
        setRuns([response.data.run, ...runs]);
        setTabValue(0);

        // Poll for completion
        const poll = setInterval(async () => {
          try {
            const statusRes = await axiosInstance.get(`api/benchmark/runs/${runId}/`);
            const run = statusRes.data.run;
            setRuns((prev) => prev.map((r) => (r.id === runId ? run : r)));

            if (run.status === 'completed' || run.status === 'failed') {
              clearInterval(poll);
              setRunning(false);
              if (run.status === 'completed') {
                setSuccess(`Benchmark completed: ${run.orders_passed} passed, ${run.orders_failed} failed`);
                setTimeout(() => setSuccess(null), 5000);
              } else {
                setError('Benchmark run failed on the server');
              }
            }
          } catch {
            clearInterval(poll);
            setRunning(false);
            setError('Lost connection while waiting for benchmark results');
          }
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start benchmark');
      setRunning(false);
    }
  };

  const handleToggleExpand = (runId) => {
    setExpandedRunId(expandedRunId === runId ? null : runId);
  };

  const handleDeleteRun = async (runId) => {
    try {
      await axiosInstance.delete(`api/benchmark/runs/${runId}/`);
      setRuns(runs.filter((r) => r.id !== runId));
      if (expandedRunId === runId) {
        setExpandedRunId(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete run');
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return (
      d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' +
      d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    );
  };

  // ── Derived data for overview ──

  const successfulRuns = runs.filter((r) => r.orders_passed > 0);
  const latestRun = successfulRuns.length > 0 ? successfulRuns[0] : null;
  const previousRun = successfulRuns.length > 1 ? successfulRuns[1] : null;

  const mapeDelta =
    latestRun && previousRun
      ? latestRun.mean_percentage_error - previousRun.mean_percentage_error
      : null;

  const maeDelta =
    latestRun && previousRun
      ? latestRun.mean_absolute_error - previousRun.mean_absolute_error
      : null;

  const passRate =
    latestRun && latestRun.total_orders > 0
      ? (latestRun.orders_passed / latestRun.total_orders) * 100
      : null;

  const previousPassRate =
    previousRun && previousRun.total_orders > 0
      ? (previousRun.orders_passed / previousRun.total_orders) * 100
      : null;

  const passRateDelta =
    passRate !== null && previousPassRate !== null ? passRate - previousPassRate : null;

  const runTimeDelta =
    latestRun?.run_time_ms != null && previousRun?.run_time_ms != null
      ? latestRun.run_time_ms - previousRun.run_time_ms
      : null;

  // Biggest deviations from latest run (all, sorted by error)
  const allDeviations = useMemo(() =>
    latestRun?.results
      ? [...latestRun.results]
          .filter((r) => r.status === 'success' && r.percentage_error !== null)
          .sort((a, b) => (b.percentage_error ?? 0) - (a.percentage_error ?? 0))
      : [],
    [latestRun]
  );
  const [deviationPage, setDeviationPage] = useState(0);
  const DEVIATIONS_PER_PAGE = 25;
  const deviationPageCount = Math.ceil(allDeviations.length / DEVIATIONS_PER_PAGE);
  const biggestDeviations = allDeviations.slice(
    deviationPage * DEVIATIONS_PER_PAGE,
    (deviationPage + 1) * DEVIATIONS_PER_PAGE
  );

  // Per-carrier stats from latest run
  const carrierStats = useMemo(() => {
    if (!latestRun?.results) return [];
    const byCarrier = {};
    for (const r of latestRun.results) {
      if (r.status !== 'success' || r.percentage_error == null) continue;
      const c = r.carrier || 'Unknown';
      if (!byCarrier[c]) byCarrier[c] = { count: 0, totalPaid: 0, totalSim: 0, absErrors: [], signedErrors: [] };
      byCarrier[c].count++;
      byCarrier[c].totalPaid += r.expected_shipping_cost;
      byCarrier[c].totalSim += r.model_shipping_cost || 0;
      byCarrier[c].absErrors.push(Math.abs(r.cost_difference || 0));
      byCarrier[c].signedErrors.push(r.cost_difference || 0);
    }
    return Object.entries(byCarrier)
      .map(([carrier, d]) => ({
        carrier,
        count: d.count,
        totalPaid: d.totalPaid,
        totalSim: d.totalSim,
        mae: d.absErrors.reduce((a, b) => a + b, 0) / d.absErrors.length,
        avgSignedError: d.signedErrors.reduce((a, b) => a + b, 0) / d.signedErrors.length,
        mape: d.absErrors.length > 0
          ? d.absErrors.reduce((sum, e, i) => sum + (e / (latestRun.results.find((_, idx) => true) ? 1 : 1)), 0)
          : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [latestRun]);

  // Per-country stats from latest run
  const countryStats = useMemo(() => {
    if (!latestRun?.results) return [];
    const byCountry = {};
    for (const r of latestRun.results) {
      if (r.status !== 'success' || r.percentage_error == null) continue;
      const c = r.shipping_country || 'Unknown';
      if (!byCountry[c]) byCountry[c] = { count: 0, totalPaid: 0, totalSim: 0, absErrors: [], pctErrors: [], signedErrors: [] };
      byCountry[c].count++;
      byCountry[c].totalPaid += r.expected_shipping_cost;
      byCountry[c].totalSim += r.model_shipping_cost || 0;
      byCountry[c].absErrors.push(Math.abs(r.cost_difference || 0));
      byCountry[c].pctErrors.push(r.percentage_error);
      byCountry[c].signedErrors.push(r.cost_difference || 0);
    }
    return Object.entries(byCountry)
      .map(([country, d]) => ({
        country,
        count: d.count,
        totalPaid: d.totalPaid,
        totalSim: d.totalSim,
        mae: d.absErrors.reduce((a, b) => a + b, 0) / d.absErrors.length,
        mape: d.pctErrors.reduce((a, b) => a + b, 0) / d.pctErrors.length,
        avgSignedError: d.signedErrors.reduce((a, b) => a + b, 0) / d.signedErrors.length,
      }))
      .sort((a, b) => b.count - a.count);
  }, [latestRun]);

  // Tab definitions
  const tabs = [
    { label: 'Performance', icon: LayoutDashboard },
    { label: `Test Cases (${testCases.length})`, icon: List },
    { label: `History (${runs.length})`, icon: History },
  ];

  return (
    <div className="px-6 py-8">
      <h1 className="text-2xl font-bold text-foreground">Benchmark</h1>
      <p className="mt-1 mb-4 text-sm text-muted-foreground">
        Test the packing model against orders with known shipping costs. Track performance across
        model versions.
      </p>

      {/* ── Alerts ── */}
      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-center justify-between rounded-md border-l-4 border-green-500 bg-green-50 p-3 text-sm text-green-800 dark:bg-green-950 dark:text-green-200">
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="ml-2 text-green-500 hover:text-green-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Loading bar */}
      {loading && (
        <div className="mb-4 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/2 animate-pulse rounded-full bg-primary" />
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="border-b border-border">
        <div className="flex gap-0">
          {tabs.map((tab, idx) => {
            const Icon = tab.icon;
            const isActive = tabValue === idx;
            return (
              <button
                key={idx}
                onClick={() => setTabValue(idx)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-b-2 border-primary text-primary'
                    : 'border-b-2 border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────
          Tab 1: Performance Overview
         ──────────────────────────────────────────────────────────── */}
      <TabPanel value={tabValue} index={0}>
        {!latestRun ? (
          <div className="rounded-lg border border-border/70 surface-panel p-8 text-center shadow-sm">
            <h3 className="text-lg font-medium text-muted-foreground">No benchmark data yet</h3>
            <p className="mt-1 mb-4 text-sm text-muted-foreground">
              Add test cases and run your first benchmark to see performance metrics.
            </p>
            <Button
              onClick={handleRunBenchmark}
              disabled={running || testCases.length === 0}
              size="lg"
            >
              {running ? (
                <ShippySpinner size={18} className="mr-2" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              {running ? 'Running...' : 'Run Benchmark'}
            </Button>
          </div>
        ) : (
          <>
            {/* Run button + latest model info */}
            <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-border/70 surface-panel p-4 shadow-sm">
              <Button
                onClick={handleRunBenchmark}
                disabled={running || testCases.length === 0}
                size="lg"
              >
                {running ? (
                  <ShippySpinner size={18} className="mr-2" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                {running ? 'Running Benchmark...' : 'Run Benchmark'}
              </Button>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">
                  {testCases.length} test case{testCases.length !== 1 ? 's' : ''} &middot; Latest:{' '}
                  {latestRun.git_commit_message || latestRun.model_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(latestRun.run_date)} &middot;{' '}
                  <code className="rounded bg-muted px-1 py-0.5 text-xs">
                    {latestRun.git_commit_hash}
                  </code>
                </p>
              </div>
              {running && (
                <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full w-1/2 animate-pulse rounded-full bg-primary" />
                </div>
              )}
            </div>

            {/* KPI Cards */}
            <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              {/* Pass Rate */}
              <div className="rounded-lg border border-border/70 surface-panel p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Pass Rate
                </p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span
                    className={`text-3xl font-bold ${
                      passRate !== null && passRate >= 80
                        ? 'text-green-600 dark:text-green-400'
                        : passRate !== null && passRate >= 50
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {passRate !== null ? `${passRate.toFixed(0)}%` : '-'}
                  </span>
                  {passRateDelta !== null && (
                    <DeltaChip value={passRateDelta} suffix="%" goodWhenPositive />
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {latestRun.orders_passed} of {latestRun.total_orders} within 20%
                </p>
              </div>

              {/* MAPE with distribution boxplot */}
              <div className="rounded-lg border border-border/70 surface-panel p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  MAPE
                </p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-foreground">
                    {latestRun.mean_percentage_error.toFixed(1)}%
                  </span>
                  {mapeDelta !== null && (
                    <DeltaChip value={mapeDelta} suffix="%" goodWhenPositive={false} />
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Mean absolute percentage error
                </p>
                {latestRun?.results && (() => {
                  const pctErrors = latestRun.results
                    .filter(r => r.status === 'success' && r.percentage_error != null)
                    .map(r => r.percentage_error)
                    .sort((a, b) => a - b);
                  if (pctErrors.length < 3) return null;
                  const q = (arr, p) => { const i = (arr.length - 1) * p; const lo = Math.floor(i); const hi = Math.ceil(i); return lo === hi ? arr[lo] : arr[lo] * (hi - i) + arr[hi] * (i - lo); };
                  const min = pctErrors[0];
                  const q1 = q(pctErrors, 0.25);
                  const med = q(pctErrors, 0.5);
                  const q3 = q(pctErrors, 0.75);
                  const max = pctErrors[pctErrors.length - 1];
                  const capMax = Math.min(max, 200);
                  const scale = (v) => 8 + (Math.min(v, capMax) / capMax) * 184;
                  return (
                    <div className="mt-2.5">
                      <svg width="100%" height="36" viewBox="0 0 200 36" preserveAspectRatio="xMidYMid meet">
                        {/* Whisker line */}
                        <line x1={scale(min)} y1="18" x2={scale(max)} y2="18" stroke="#94a3b8" strokeWidth="1" />
                        {/* Min/Max caps */}
                        <line x1={scale(min)} y1="12" x2={scale(min)} y2="24" stroke="#94a3b8" strokeWidth="1" />
                        <line x1={scale(capMax)} y1="12" x2={scale(capMax)} y2="24" stroke="#94a3b8" strokeWidth="1" />
                        {/* IQR box */}
                        <rect x={scale(q1)} y="8" width={scale(q3) - scale(q1)} height="20" rx="2" fill="#3b82f6" fillOpacity="0.15" stroke="#3b82f6" strokeWidth="1" />
                        {/* Median line */}
                        <line x1={scale(med)} y1="8" x2={scale(med)} y2="28" stroke="#3b82f6" strokeWidth="2" />
                        {/* 20% threshold */}
                        <line x1={scale(20)} y1="4" x2={scale(20)} y2="32" stroke="#22c55e" strokeWidth="1" strokeDasharray="2,2" />
                      </svg>
                      <div className="flex justify-between text-[10px] text-muted-foreground -mt-1 px-1">
                        <span>{min.toFixed(0)}%</span>
                        <span title="Q1 / Median / Q3" className="text-blue-500 font-medium">{q1.toFixed(0)} · {med.toFixed(0)} · {q3.toFixed(0)}%</span>
                        <span>{max.toFixed(0)}%</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* MAE with under/overcharge bias */}
              <div className="rounded-lg border border-border/70 surface-panel p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  MAE
                </p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-foreground">
                    &euro;{latestRun.mean_absolute_error.toFixed(2)}
                  </span>
                  {maeDelta !== null && (
                    <DeltaChip
                      value={maeDelta}
                      prefix="EUR "
                      decimals={2}
                      goodWhenPositive={false}
                    />
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Mean absolute error</p>
                {latestRun?.results && (() => {
                  const successResults = latestRun.results.filter(r => r.status === 'success' && r.cost_difference != null);
                  if (successResults.length === 0) return null;
                  const avgBias = successResults.reduce((s, r) => s + r.cost_difference, 0) / successResults.length;
                  const overCount = successResults.filter(r => r.cost_difference > 0).length;
                  const underCount = successResults.filter(r => r.cost_difference < 0).length;
                  const isOver = avgBias > 0;
                  return (
                    <div className="mt-1.5 space-y-0.5">
                      <p className={`text-xs font-medium ${isOver ? 'text-red-600' : 'text-green-600'}`}>
                        {isOver ? '▲ Overcharges' : '▼ Undercharges'} by avg &euro;{Math.abs(avgBias).toFixed(2)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {overCount} over · {underCount} under · {successResults.length - overCount - underCount} exact
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* Total Cost */}
              <div className="rounded-lg border border-border/70 surface-panel p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Total Cost
                </p>
                <div className="mt-1 space-y-0.5">
                  <p className="text-sm">
                    Paid (Carrier):{' '}
                    <strong className="text-foreground">
                      &euro;{latestRun.total_expected_cost.toFixed(2)}
                    </strong>
                  </p>
                  <p className="text-sm">
                    Simulation:{' '}
                    <strong className="text-foreground">
                      &euro;{latestRun.total_model_cost.toFixed(2)}
                    </strong>
                  </p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Diff: &euro;
                  {(latestRun.total_model_cost - latestRun.total_expected_cost).toFixed(2)}
                </p>
              </div>

              {/* Run Time */}
              <div className="rounded-lg border border-border/70 surface-panel p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Run Time
                </p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-foreground">
                    {latestRun.run_time_ms != null
                      ? latestRun.run_time_ms >= 1000
                        ? `${(latestRun.run_time_ms / 1000).toFixed(1)}s`
                        : `${Math.round(latestRun.run_time_ms)}ms`
                      : '-'}
                  </span>
                  {runTimeDelta !== null && (
                    <DeltaChip
                      value={runTimeDelta / 1000}
                      suffix="s"
                      decimals={1}
                      goodWhenPositive={false}
                    />
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {latestRun.run_time_ms != null && latestRun.total_orders > 0
                    ? `${(latestRun.run_time_ms / 1000 / latestRun.total_orders).toFixed(1)}s per order`
                    : 'Total benchmark duration'}
                </p>
              </div>
            </div>

            {/* Charts */}
            <div className="mb-6 grid gap-4 md:grid-cols-3">
              <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm">
                <h4 className="mb-2 text-sm font-bold text-foreground">Performance Trend</h4>
                {runs.filter((r) => r.orders_passed > 0).length < 2 ? (
                  <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                    Run at least 2 benchmarks to see trends
                  </div>
                ) : (
                  <PerformanceTrendChart runs={runs} />
                )}
              </div>
              <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm">
                <h4 className="mb-2 text-sm font-bold text-foreground">
                  Paid vs Simulation Cost (Latest Run)
                </h4>
                {!latestRun?.results ||
                latestRun.results.filter((r) => r.status === 'success').length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                    No successful results to plot
                  </div>
                ) : (
                  <ActualVsModelChart results={latestRun.results.filter((r) => r.status === 'success')} />
                )}
              </div>
              <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm">
                <h4 className="mb-2 text-sm font-bold text-foreground">Time per Order Trend</h4>
                {runs.filter((r) => r.run_time_ms != null && r.orders_passed > 0).length < 2 ? (
                  <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                    Run at least 2 benchmarks with timing to see trends
                  </div>
                ) : (
                  <RunTimeTrendChart runs={runs} />
                )}
              </div>
            </div>

            {/* Biggest Deviations */}
            {biggestDeviations.length > 0 && (
              <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <h4 className="text-sm font-bold text-foreground">
                    Biggest Deviations (Latest Run)
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                          Order
                        </th>
                        <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                          Country
                        </th>
                        <th className="py-2 pr-4 text-center font-semibold text-muted-foreground">
                          Items
                        </th>
                        <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                          Paid (Carrier)
                        </th>
                        <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                          Simulation
                        </th>
                        <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                          Difference
                        </th>
                        <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                          Error %
                        </th>
                        <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                          Strategy
                        </th>
                        <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                          Time
                        </th>
                        <th className="w-[40px] py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {biggestDeviations.map((r) => (
                        <tr key={r.id} className="border-b border-border last:border-0">
                          <td className="py-2 pr-4">{r.order_number}</td>
                          <td className="py-2 pr-4">{r.shipping_country || '-'}</td>
                          <td className="py-2 pr-4 text-center">{r.total_items}</td>
                          <td className="py-2 pr-4 text-right">
                            &euro;{r.expected_shipping_cost.toFixed(2)}
                          </td>
                          <td className="py-2 pr-4 text-right">
                            {r.model_shipping_cost !== null
                              ? `\u20AC${r.model_shipping_cost.toFixed(2)}`
                              : '-'}
                          </td>
                          <td
                            className={`py-2 pr-4 text-right font-medium ${
                              r.cost_difference !== null
                                ? r.cost_difference > 0
                                  ? 'text-red-600'
                                  : 'text-green-600'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {r.cost_difference !== null
                              ? `${r.cost_difference > 0 ? '+' : ''}\u20AC${r.cost_difference.toFixed(2)}`
                              : '-'}
                          </td>
                          <td
                            className={`py-2 pr-4 text-right font-semibold ${
                              r.percentage_error !== null
                                ? r.percentage_error <= 20
                                  ? 'text-green-600'
                                  : 'text-red-600'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {r.percentage_error !== null
                              ? `${r.percentage_error.toFixed(1)}%`
                              : '-'}
                          </td>
                          <td
                            className="max-w-[200px] truncate py-2 pr-4"
                            title={r.strategy_used || ''}
                          >
                            {r.strategy_used || '-'}
                          </td>
                          <td
                            className={`py-2 pr-4 text-right tabular-nums ${r.processing_time_ms != null && r.processing_time_ms > 1000 ? 'font-semibold text-red-600' : 'text-muted-foreground'}`}
                            title={r.order_data?.timing ? `Shopify: ${r.order_data.timing.shopify_fetch_ms}ms | Dims: ${r.order_data.timing.dimension_lookup_ms}ms | Packing: ${r.order_data.timing.packing_ms}ms` : ''}
                          >
                            {r.processing_time_ms != null ? (
                              <span className="group relative cursor-help">
                                {(r.processing_time_ms / 1000).toFixed(2)}s
                                {r.order_data?.timing && (
                                  <TimingBreakdownBar timing={r.order_data.timing} totalMs={r.processing_time_ms} />
                                )}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="py-2 text-center">
                            <button
                              onClick={() => navigate(`/packing-simulator?order=${(r.order_number || '').replace('#', '')}`)}
                              className="rounded p-1 text-primary transition-colors hover:bg-primary/10"
                              title="Simulate in Packing Simulator"
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {deviationPageCount > 1 && (
                  <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                    <p className="text-xs text-muted-foreground">
                      Showing {deviationPage * DEVIATIONS_PER_PAGE + 1}–
                      {Math.min((deviationPage + 1) * DEVIATIONS_PER_PAGE, allDeviations.length)} of{' '}
                      {allDeviations.length} deviations
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeviationPage((p) => Math.max(0, p - 1))}
                        disabled={deviationPage === 0}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeviationPage((p) => Math.min(deviationPageCount - 1, p + 1))}
                        disabled={deviationPage >= deviationPageCount - 1}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Per-Carrier & Per-Country Breakdown */}
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {/* Per-Carrier */}
              {carrierStats.length > 0 && (
                <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm">
                  <h4 className="mb-3 text-sm font-bold text-foreground">Per Carrier</h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 pr-3 text-left font-semibold text-muted-foreground">Carrier</th>
                        <th className="py-2 pr-3 text-center font-semibold text-muted-foreground">Orders</th>
                        <th className="py-2 pr-3 text-right font-semibold text-muted-foreground">MAE</th>
                        <th className="py-2 text-right font-semibold text-muted-foreground">Avg Bias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carrierStats.map((s) => (
                        <tr key={s.carrier} className="border-b border-border last:border-0">
                          <td className="py-2 pr-3 font-medium">{s.carrier}</td>
                          <td className="py-2 pr-3 text-center tabular-nums">{s.count}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">&euro;{s.mae.toFixed(2)}</td>
                          <td className={`py-2 text-right tabular-nums font-medium ${s.avgSignedError > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {s.avgSignedError > 0 ? '+' : ''}&euro;{s.avgSignedError.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Per-Country */}
              {countryStats.length > 0 && (
                <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm">
                  <h4 className="mb-3 text-sm font-bold text-foreground">Per Country</h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 pr-3 text-left font-semibold text-muted-foreground">Country</th>
                        <th className="py-2 pr-3 text-center font-semibold text-muted-foreground">Orders</th>
                        <th className="py-2 pr-3 text-right font-semibold text-muted-foreground">MAE</th>
                        <th className="py-2 pr-3 text-right font-semibold text-muted-foreground">MAPE</th>
                        <th className="py-2 text-right font-semibold text-muted-foreground">Avg Bias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {countryStats.map((s) => (
                        <tr key={s.country} className="border-b border-border last:border-0">
                          <td className="py-2 pr-3 font-medium">{s.country}</td>
                          <td className="py-2 pr-3 text-center tabular-nums">{s.count}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">&euro;{s.mae.toFixed(2)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{s.mape.toFixed(1)}%</td>
                          <td className={`py-2 text-right tabular-nums font-medium ${s.avgSignedError > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {s.avgSignedError > 0 ? '+' : ''}&euro;{s.avgSignedError.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </TabPanel>

      {/* ────────────────────────────────────────────────────────────
          Tab 2: Test Cases
         ──────────────────────────────────────────────────────────── */}
      <TabPanel value={tabValue} index={1}>
        <div className="rounded-lg border border-border/70 surface-panel p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Test Cases</h3>
            <p className="text-sm text-muted-foreground">
              {testCases.length} order{testCases.length !== 1 ? 's' : ''} configured
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                    Order Number
                  </th>
                  <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                    Paid Cost
                  </th>
                  <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                    Carrier
                  </th>
                  <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                    Notes
                  </th>
                  <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                    Added
                  </th>
                  <th className="w-[60px] py-2" />
                </tr>
              </thead>
              <tbody>
                {testCases.map((tc) => {
                  const isEditing = editingId === tc.id;
                  return (
                    <tr
                      key={tc.id}
                      className="border-b border-border transition-colors hover:bg-muted/50"
                    >
                      <td className="py-2 pr-4 font-medium">
                        {isEditing ? (
                          <Input
                            value={editValues.order_number}
                            onChange={(e) => setEditValues({ ...editValues, order_number: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTestCase(); if (e.key === 'Escape') cancelEditing(); }}
                            className="h-8"
                          />
                        ) : tc.order_number}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {isEditing ? (
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editValues.expected_shipping_cost}
                            onChange={(e) => setEditValues({ ...editValues, expected_shipping_cost: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTestCase(); if (e.key === 'Escape') cancelEditing(); }}
                            className="ml-auto h-8 w-[120px]"
                          />
                        ) : `\u20AC${tc.expected_shipping_cost.toFixed(2)}`}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {isEditing ? (
                          <Input
                            value={editValues.carrier}
                            onChange={(e) => setEditValues({ ...editValues, carrier: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTestCase(); if (e.key === 'Escape') cancelEditing(); }}
                            className="h-8 w-[100px]"
                            placeholder="e.g. bpost"
                          />
                        ) : tc.carrier || '-'}
                      </td>
                      <td className="max-w-[300px] truncate py-2 pr-4 text-muted-foreground">
                        {isEditing ? (
                          <Input
                            value={editValues.notes}
                            onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTestCase(); if (e.key === 'Escape') cancelEditing(); }}
                            className="h-8"
                          />
                        ) : tc.notes || '-'}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {new Date(tc.created_at).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                        })}
                      </td>
                      <td className="py-2 text-center">
                        <div className="flex items-center justify-center gap-0.5">
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleSaveTestCase}
                                className="rounded p-1 text-green-600 transition-colors hover:bg-green-50 hover:text-green-700 dark:hover:bg-green-950"
                                title="Save"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={cancelEditing}
                                className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted"
                                title="Cancel"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => startEditing(tc)}
                                className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                title="Edit"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteTestCase(tc.id)}
                                className="rounded p-1 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {/* Add new test case row */}
                <tr>
                  <td className="py-2 pr-4">
                    <Input
                      placeholder="e.g. LAMP-12345"
                      value={newOrderNumber}
                      onChange={(e) => setNewOrderNumber(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTestCase()}
                      className="h-9"
                    />
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <Input
                      placeholder="0.00"
                      type="number"
                      step="0.01"
                      min="0"
                      value={newExpectedCost}
                      onChange={(e) => setNewExpectedCost(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTestCase()}
                      className="ml-auto h-9 w-[120px]"
                    />
                  </td>
                  <td className="py-2 pr-4">
                    <Input
                      placeholder="e.g. bpost"
                      value={newCarrier}
                      onChange={(e) => setNewCarrier(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTestCase()}
                      className="h-9 w-[100px]"
                    />
                  </td>
                  <td className="py-2 pr-4">
                    <Input
                      placeholder="Optional notes"
                      value={newNotes}
                      onChange={(e) => setNewNotes(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTestCase()}
                      className="h-9"
                    />
                  </td>
                  <td className="py-2 pr-4" />
                  <td className="py-2 text-center">
                    <button
                      onClick={handleAddTestCase}
                      disabled={!newOrderNumber.trim() || !newExpectedCost.trim()}
                      className="rounded p-1 text-primary transition-colors hover:bg-primary/10 disabled:pointer-events-none disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </TabPanel>

      {/* ────────────────────────────────────────────────────────────
          Tab 3: Benchmark History
         ──────────────────────────────────────────────────────────── */}
      <TabPanel value={tabValue} index={2}>
        <div className="rounded-lg border border-border/70 surface-panel p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold text-foreground">Benchmark History</h3>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No benchmark runs yet. Add test cases and click &ldquo;Run Benchmark&rdquo; to get
              started.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="w-[40px] py-2" />
                    <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                      Model
                    </th>
                    <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                      Date
                    </th>
                    <th className="py-2 pr-4 text-center font-semibold text-muted-foreground">
                      Orders
                    </th>
                    <th className="py-2 pr-4 text-center font-semibold text-muted-foreground">
                      Passed
                    </th>
                    <th className="py-2 pr-4 text-center font-semibold text-muted-foreground">
                      Failed
                    </th>
                    <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                      MAE
                    </th>
                    <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                      MAPE
                    </th>
                    <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                      Total Paid
                    </th>
                    <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                      Total Simulated
                    </th>
                    <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                      Time
                    </th>
                    <th className="w-[60px] py-2" />
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <React.Fragment key={run.id}>
                      <tr
                        className={`cursor-pointer border-b border-border transition-colors hover:bg-muted/50 ${
                          expandedRunId === run.id ? 'border-b-0' : ''
                        }`}
                        onClick={() => handleToggleExpand(run.id)}
                      >
                        <td className="py-2">
                          <button className="rounded p-1 hover:bg-muted">
                            {expandedRunId === run.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                        <td className="max-w-[320px] truncate py-2 pr-4">
                          <span className="font-medium">
                            <code className="text-xs text-muted-foreground">
                              {run.git_commit_hash}
                            </code>
                            {run.git_commit_hash && ' - '}
                            {run.git_commit_message || run.model_name}
                          </span>
                        </td>
                        <td className="py-2 pr-4 whitespace-nowrap">{formatDate(run.run_date)}</td>
                        <td className="py-2 pr-4 text-center tabular-nums">
                          {run.total_orders}
                        </td>
                        <td className="py-2 pr-4 text-center">
                          <span className="inline-flex items-center rounded-full border border-green-300 px-2 py-0.5 text-xs font-medium text-green-700 dark:border-green-700 dark:text-green-400">
                            {run.orders_passed}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-center">
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                              run.orders_failed > 0
                                ? 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-400'
                                : 'border-border text-muted-foreground'
                            }`}
                          >
                            {run.orders_failed}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          &euro;{run.mean_absolute_error.toFixed(2)}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          {run.mean_percentage_error.toFixed(1)}%
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          &euro;{run.total_expected_cost.toFixed(2)}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          &euro;{run.total_model_cost.toFixed(2)}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums text-xs">
                          {run.run_time_ms != null
                            ? run.run_time_ms >= 1000
                              ? `${(run.run_time_ms / 1000).toFixed(1)}s`
                              : `${Math.round(run.run_time_ms)}ms`
                            : '-'}
                        </td>
                        <td
                          className="py-2 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => handleDeleteRun(run.id)}
                            className="rounded p-1 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>

                      {/* Expandable per-order results */}
                      {expandedRunId === run.id && (
                        <tr>
                          <td colSpan={12} className="p-0">
                            <div className="border-b border-border bg-muted/30 p-4">
                              <h4 className="mb-2 text-sm font-bold text-foreground">
                                Per-Order Results
                              </h4>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-border">
                                    <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                                      Order
                                    </th>
                                    <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                                      Country
                                    </th>
                                    <th className="py-2 pr-4 text-center font-semibold text-muted-foreground">
                                      Items
                                    </th>
                                    <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                                      Paid (Carrier)
                                    </th>
                                    <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                                      Simulation
                                    </th>
                                    <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                                      Difference
                                    </th>
                                    <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                                      Error %
                                    </th>
                                    <th className="py-2 pr-4 text-left font-semibold text-muted-foreground">
                                      Strategy
                                    </th>
                                    <th className="py-2 pr-4 text-right font-semibold text-muted-foreground">
                                      Time
                                    </th>
                                    <th className="py-2 text-center font-semibold text-muted-foreground">
                                      Status
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(run.results || []).map((r) => {
                                    const isPass =
                                      r.status === 'success' &&
                                      r.percentage_error !== null &&
                                      r.percentage_error <= 20;
                                    return (
                                      <tr
                                        key={r.id}
                                        className="border-b border-border last:border-0"
                                      >
                                        <td className="py-2 pr-4">{r.order_number}</td>
                                        <td className="py-2 pr-4">
                                          {r.shipping_country || '-'}
                                        </td>
                                        <td className="py-2 pr-4 text-center tabular-nums">
                                          {r.total_items}
                                        </td>
                                        <td className="py-2 pr-4 text-right tabular-nums">
                                          &euro;{r.expected_shipping_cost.toFixed(2)}
                                        </td>
                                        <td className="py-2 pr-4 text-right tabular-nums">
                                          {r.model_shipping_cost !== null
                                            ? `\u20AC${r.model_shipping_cost.toFixed(2)}`
                                            : '-'}
                                        </td>
                                        <td
                                          className={`py-2 pr-4 text-right font-medium tabular-nums ${
                                            r.cost_difference !== null
                                              ? r.cost_difference > 0
                                                ? 'text-red-600'
                                                : 'text-green-600'
                                              : 'text-muted-foreground'
                                          }`}
                                        >
                                          {r.cost_difference !== null
                                            ? `${r.cost_difference > 0 ? '+' : ''}\u20AC${r.cost_difference.toFixed(2)}`
                                            : '-'}
                                        </td>
                                        <td
                                          className={`py-2 pr-4 text-right font-medium tabular-nums ${
                                            r.percentage_error !== null
                                              ? r.percentage_error <= 20
                                                ? 'text-green-600'
                                                : 'text-red-600'
                                              : 'text-muted-foreground'
                                          }`}
                                        >
                                          {r.percentage_error !== null
                                            ? `${r.percentage_error.toFixed(1)}%`
                                            : '-'}
                                        </td>
                                        <td
                                          className="max-w-[200px] truncate py-2 pr-4"
                                          title={r.strategy_used || r.error_message || ''}
                                        >
                                          {r.strategy_used || r.error_message || '-'}
                                        </td>
                                        <td
                                          className={`py-2 pr-4 text-right tabular-nums ${r.processing_time_ms != null && r.processing_time_ms > 1000 ? 'font-semibold text-red-600' : 'text-muted-foreground'}`}
                                          title={r.order_data?.timing ? `Shopify: ${r.order_data.timing.shopify_fetch_ms}ms | Dims: ${r.order_data.timing.dimension_lookup_ms}ms | Packing: ${r.order_data.timing.packing_ms}ms` : ''}
                                        >
                                          {r.processing_time_ms != null ? (
                                            <span className="group relative cursor-help">
                                              {(r.processing_time_ms / 1000).toFixed(2)}s
                                              {r.order_data?.timing && (
                                                <TimingBreakdownBar timing={r.order_data.timing} totalMs={r.processing_time_ms} />
                                              )}
                                            </span>
                                          ) : '-'}
                                        </td>
                                        <td className="py-2 text-center">
                                          {r.status === 'success' ? (
                                            isPass ? (
                                              <CheckCircle className="mx-auto h-4 w-4 text-green-600" />
                                            ) : (
                                              <XCircle className="mx-auto h-4 w-4 text-red-600" />
                                            )
                                          ) : (
                                            <span className="inline-flex items-center rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:border-red-700 dark:bg-red-950 dark:text-red-400">
                                              Error
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </TabPanel>
    </div>
  );
};

/**
 * Tiny stacked bar showing Shopify / Dims / Packing time split beneath the time value.
 */
function TimingBreakdownBar({ timing, totalMs }) {
  if (!timing || totalMs <= 0) return null;
  const shopify = timing.shopify_fetch_ms || 0;
  const dims = timing.dimension_lookup_ms || 0;
  const packing = timing.packing_ms || 0;
  const sum = shopify + dims + packing;
  if (sum <= 0) return null;
  return (
    <span className="mt-0.5 flex h-1 w-full overflow-hidden rounded-full">
      <span className="bg-amber-500" style={{ width: `${(shopify / sum) * 100}%` }} />
      <span className="bg-sky-500" style={{ width: `${(dims / sum) * 100}%` }} />
      <span className="bg-violet-500" style={{ width: `${(packing / sum) * 100}%` }} />
    </span>
  );
}

/**
 * Small delta chip showing a trend indicator (up/down arrow + value).
 * goodWhenPositive: if true, positive values render green; if false, negative values render green.
 */
function DeltaChip({ value, suffix = '', prefix = '', decimals = 0, goodWhenPositive = true }) {
  const isGood = goodWhenPositive ? value >= 0 : value <= 0;
  const formatted = `${value >= 0 ? '+' : ''}${prefix}${
    decimals > 0 ? value.toFixed(decimals) : value.toFixed(0)
  }${suffix}`;

  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-xs font-medium ${
        isGood
          ? 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-400'
          : 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-400'
      }`}
    >
      {isGood ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {formatted}
    </span>
  );
}

/**
 * SVG line chart showing MAPE and Pass Rate trends across benchmark runs.
 */
function PerformanceTrendChart({ runs }) {
  const sorted = [...runs].filter((r) => r.orders_passed > 0).reverse(); // oldest first, skip fully-failed
  const W = 500, H = 200, PL = 45, PR = 15, PT = 15, PB = 30;
  const cw = W - PL - PR, ch = H - PT - PB;

  const mapeValues = sorted.map((r) => r.mean_percentage_error);
  const passValues = sorted.map((r) =>
    r.total_orders > 0 ? (r.orders_passed / r.total_orders) * 100 : 0
  );

  const mapeMax = Math.max(...mapeValues, 10);
  const yMax = Math.ceil(mapeMax / 10) * 10;

  const xStep = sorted.length > 1 ? cw / (sorted.length - 1) : cw / 2;

  const mapePoints = mapeValues.map((v, i) => ({
    x: PL + i * xStep,
    y: PT + ch - (v / yMax) * ch,
  }));
  const passPoints = passValues.map((v, i) => ({
    x: PL + i * xStep,
    y: PT + ch - (v / 100) * ch,
  }));

  const toPath = (pts) => pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  const gridLines = [0, 25, 50, 75, 100].map((pct) => ({
    y: PT + ch - (pct / 100) * ch,
    label: `${((pct / 100) * yMax).toFixed(0)}%`,
  }));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 220 }}>
      {/* Grid */}
      {gridLines.map((g, i) => (
        <g key={i}>
          <line x1={PL} y1={g.y} x2={W - PR} y2={g.y} stroke="currentColor" strokeOpacity={0.1} />
          <text x={PL - 4} y={g.y + 3} textAnchor="end" className="fill-muted-foreground" style={{ fontSize: 9 }}>
            {g.label}
          </text>
        </g>
      ))}
      {/* X axis labels */}
      {sorted.map((r, i) => (
        <text
          key={i}
          x={PL + i * xStep}
          y={H - 4}
          textAnchor="middle"
          className="fill-muted-foreground"
          style={{ fontSize: 8 }}
        >
          {r.git_commit_hash?.slice(0, 5) || `#${i + 1}`}
        </text>
      ))}
      {/* Pass Rate line (green) */}
      <path d={toPath(passPoints)} fill="none" stroke="#22c55e" strokeWidth={2} strokeLinejoin="round" />
      {passPoints.map((p, i) => (
        <circle key={`p${i}`} cx={p.x} cy={p.y} r={3} fill="#22c55e" />
      ))}
      {/* MAPE line (blue) */}
      <path d={toPath(mapePoints)} fill="none" stroke="#3b82f6" strokeWidth={2} strokeLinejoin="round" />
      {mapePoints.map((p, i) => (
        <circle key={`m${i}`} cx={p.x} cy={p.y} r={3} fill="#3b82f6" />
      ))}
      {/* Legend */}
      <circle cx={PL + 10} cy={PT + 2} r={3} fill="#3b82f6" />
      <text x={PL + 17} y={PT + 5} className="fill-foreground" style={{ fontSize: 9 }}>MAPE</text>
      <circle cx={PL + 60} cy={PT + 2} r={3} fill="#22c55e" />
      <text x={PL + 67} y={PT + 5} className="fill-foreground" style={{ fontSize: 9 }}>Pass Rate</text>
    </svg>
  );
}

/**
 * SVG line chart showing time per order across benchmark runs.
 */
function RunTimeTrendChart({ runs }) {
  const sorted = [...runs].filter((r) => r.run_time_ms != null && r.orders_passed > 0).reverse();
  if (sorted.length < 2) return null;

  const W = 500, H = 200, PL = 55, PR = 15, PT = 15, PB = 30;
  const cw = W - PL - PR, ch = H - PT - PB;

  const values = sorted.map((r) => r.total_orders > 0 ? r.run_time_ms / 1000 / r.total_orders : 0);

  // Nice Y-axis max: round up to nearest 0.5s
  const rawMax = Math.max(...values, 0.5);
  const yMax = Math.ceil(rawMax * 2) / 2;

  const xStep = sorted.length > 1 ? cw / (sorted.length - 1) : cw / 2;

  const points = values.map((v, i) => ({
    x: PL + i * xStep,
    y: PT + ch - (v / yMax) * ch,
  }));

  const toPath = (pts) => pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  const gridLines = [0, 25, 50, 75, 100].map((pct) => ({
    y: PT + ch - (pct / 100) * ch,
    label: `${((pct / 100) * yMax).toFixed(1)}s`,
  }));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 220 }}>
      {gridLines.map((g, i) => (
        <g key={i}>
          <line x1={PL} y1={g.y} x2={W - PR} y2={g.y} stroke="currentColor" strokeOpacity={0.1} />
          <text x={PL - 4} y={g.y + 3} textAnchor="end" className="fill-muted-foreground" style={{ fontSize: 9 }}>
            {g.label}
          </text>
        </g>
      ))}
      {sorted.map((r, i) => (
        <text key={i} x={PL + i * xStep} y={H - 4} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 8 }}>
          {r.git_commit_hash?.slice(0, 5) || `#${i + 1}`}
        </text>
      ))}
      {/* Per-order time area fill */}
      <path
        d={`${toPath(points)} L${points[points.length - 1].x},${PT + ch} L${points[0].x},${PT + ch} Z`}
        fill="#8b5cf6" fillOpacity={0.1}
      />
      {/* Per-order time line */}
      <path d={toPath(points)} fill="none" stroke="#8b5cf6" strokeWidth={2} strokeLinejoin="round" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={3} fill="#8b5cf6" />
          <text x={p.x} y={p.y - 6} textAnchor="middle" className="fill-foreground" style={{ fontSize: 8, fontWeight: 600 }}>
            {values[i].toFixed(2)}s
          </text>
        </g>
      ))}
      {/* Label */}
      <circle cx={PL + 10} cy={PT + 2} r={3} fill="#8b5cf6" />
      <text x={PL + 17} y={PT + 5} className="fill-foreground" style={{ fontSize: 9 }}>Time per Order</text>
    </svg>
  );
}

/**
 * SVG scatter plot: Actual cost (x-axis) vs Model cost (y-axis) per order.
 * Diagonal line = perfect prediction. Points above = model overestimates, below = underestimates.
 */
function ActualVsModelChart({ results }) {
  const W = 500, H = 250, PL = 45, PR = 20, PT = 15, PB = 35;
  const cw = W - PL - PR, ch = H - PT - PB;

  const allVals = results.flatMap((r) => [r.expected_shipping_cost, r.model_shipping_cost || 0]);
  const maxVal = Math.max(...allVals, 1);
  const axisMax = Math.ceil(maxVal / 10) * 10 || 10;

  const toX = (v) => PL + (v / axisMax) * cw;
  const toY = (v) => PT + ch - (v / axisMax) * ch;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((pct) => pct * axisMax);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 270 }}>
      {/* Grid lines */}
      {ticks.map((v, i) => (
        <g key={i}>
          <line x1={toX(v)} y1={PT} x2={toX(v)} y2={PT + ch} stroke="currentColor" strokeOpacity={0.07} />
          <line x1={PL} y1={toY(v)} x2={PL + cw} y2={toY(v)} stroke="currentColor" strokeOpacity={0.07} />
          <text x={toX(v)} y={H - 10} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 9 }}>
            {`\u20AC${v.toFixed(0)}`}
          </text>
          <text x={PL - 4} y={toY(v) + 3} textAnchor="end" className="fill-muted-foreground" style={{ fontSize: 9 }}>
            {`\u20AC${v.toFixed(0)}`}
          </text>
        </g>
      ))}
      {/* Perfect prediction diagonal */}
      <line x1={toX(0)} y1={toY(0)} x2={toX(axisMax)} y2={toY(axisMax)} stroke="currentColor" strokeOpacity={0.2} strokeDasharray="4,3" strokeWidth={1} />
      {/* Scatter points */}
      {results.map((r, i) => {
        const actual = r.expected_shipping_cost;
        const model = r.model_shipping_cost || 0;
        const isOver = model > actual;
        const isPass = r.percentage_error !== null && r.percentage_error <= 20;
        return (
          <g key={i}>
            <circle
              cx={toX(actual)}
              cy={toY(model)}
              r={5}
              fill={isPass ? '#22c55e' : '#ef4444'}
              fillOpacity={0.8}
              stroke={isPass ? '#16a34a' : '#dc2626'}
              strokeWidth={1}
            />
            <text
              x={toX(actual) + 7}
              y={toY(model) + 3}
              className="fill-muted-foreground"
              style={{ fontSize: 8 }}
            >
              {r.order_number}
            </text>
          </g>
        );
      })}
      {/* Axis labels */}
      <text x={PL + cw / 2} y={H - 0} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 10 }}>
        Paid Cost (Carrier)
      </text>
      <text x={12} y={PT + ch / 2} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 10 }} transform={`rotate(-90, 12, ${PT + ch / 2})`}>
        Simulation Cost
      </text>
      {/* Legend */}
      <circle cx={PL + cw - 120} cy={PT + 4} r={4} fill="#22c55e" fillOpacity={0.8} />
      <text x={PL + cw - 113} y={PT + 7} className="fill-foreground" style={{ fontSize: 9 }}>Pass (&le;20%)</text>
      <circle cx={PL + cw - 45} cy={PT + 4} r={4} fill="#ef4444" fillOpacity={0.8} />
      <text x={PL + cw - 38} y={PT + 7} className="fill-foreground" style={{ fontSize: 9 }}>Fail</text>
    </svg>
  );
}

export default Benchmark;
